import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { FileViewer } from '@capacitor/file-viewer';
import { detectPlatform } from './session-storage';

/**
 * ReportFilePort — HANDING A GENERATED DOCUMENT TO THE DEVICE (M10 / S10-04).
 *
 * This is the ports layer (ARCHITECTURE §3, INV-T20): `src/platform/` is the
 * only place `@capacitor/*` may be imported, and feature code asks for the
 * capability rather than for three plugins.
 *
 * **WHY A FILE EXISTS AT ALL.** Android cannot share bytes that live only in a
 * JavaScript variable. The share sheet takes a URI, and a URI needs a real file
 * — which is why writing, viewing and sharing are one port rather than three:
 * they all operate on the SAME artifact, and separating them would invite two
 * of them to disagree about which file they meant.
 *
 * **THE FILE IS DISPOSABLE AND ITS NAME SAYS SO.** It goes to the OS cache
 * directory under a fixed `crewlink-report-` prefix, carrying no Capture id, no
 * Project UUID, no user identifier and no token. The cache is reclaimable by the
 * system, so nothing here can quietly become durable report storage — **saved
 * reports are S10-05**, and the sweep below only ever touches files this prefix
 * created.
 *
 * **CANCELLING A SHARE IS NOT A FAILURE**, and the distinction is made here
 * rather than in the UI: the Android plugin rejects with `Share canceled` when
 * the user dismisses the sheet, which is indistinguishable from a real error to
 * any caller that only sees a rejected promise.
 */

/** The one true prefix. Cleanup matches on it EXACTLY and on nothing else. */
export const REPORT_TEMP_PREFIX = 'crewlink-report-';

/** Old artifacts are swept after a day — long past any share still in flight. */
export const REPORT_TEMP_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export interface ReportFileHandle {
  /** What the share sheet and the viewer are given. */
  readonly uri: string;
  /** Cache-relative name, for deletion and sweeping. Never shown to anyone. */
  readonly path: string;
}

export type ShareOutcome = 'shared' | 'cancelled';

export interface ReportFilePort {
  readonly name: string;
  /** Whether this runtime can hand a file to a system share sheet at all. */
  canShare(): Promise<boolean>;
  write(filename: string, bytes: Uint8Array): Promise<ReportFileHandle>;
  view(handle: ReportFileHandle): Promise<void>;
  share(handle: ReportFileHandle, options: { title: string }): Promise<ShareOutcome>;
  /** Removes one artifact. Never throws — cleanup is not a user-facing task. */
  discard(handle: ReportFileHandle): Promise<void>;
  /** Removes OUR stale artifacts only. Returns how many were removed. */
  sweepStale(now: number, keep?: ReportFileHandle | null): Promise<number>;
}

const PDF_MIME = 'application/pdf';

function base64From(bytes: Uint8Array): string {
  let binary = '';
  const CHUNK = 0x8000;
  for (let index = 0; index < bytes.length; index += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(index, index + CHUNK));
  }
  return btoa(binary);
}

/** `crewlink-report-<millis>.pdf` — disposable, and deliberately anonymous. */
export function temporaryReportPath(now: number): string {
  return `${REPORT_TEMP_PREFIX}${String(now)}.pdf`;
}

/** The plugin slices this port uses. Structural, so every path is provable. */
export interface FilesystemLike {
  writeFile(options: {
    path: string;
    data: string;
    directory: Directory;
    recursive?: boolean;
  }): Promise<{ uri: string }>;
  deleteFile(options: { path: string; directory: Directory }): Promise<void>;
  readdir(options: {
    path: string;
    directory: Directory;
  }): Promise<{ files: { name: string; type: 'directory' | 'file'; mtime: number }[] }>;
}

export interface ShareLike {
  canShare(): Promise<{ value: boolean }>;
  share(options: {
    title?: string;
    dialogTitle?: string;
    files?: string[];
  }): Promise<{ activityType?: string }>;
}

export interface FileViewerLike {
  openDocumentFromLocalPath(options: { path: string }): Promise<void>;
}

/**
 * A rejected share that is really a dismissal.
 *
 * The Android plugin rejects with `Share canceled` (its spelling) when the
 * chooser returns `RESULT_CANCELED`; iOS uses the same wording. Treating that as
 * an error would tell Brandon his report failed when he simply changed his mind.
 */
export function isShareCancellation(error: unknown): boolean {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  return /share cancel+ed/i.test(message);
}

export function createCapacitorReportFile(
  plugins: {
    filesystem: FilesystemLike;
    share: ShareLike;
    viewer: FileViewerLike;
  } = { filesystem: Filesystem, share: Share, viewer: FileViewer },
): ReportFilePort {
  const { filesystem, share, viewer } = plugins;

  return {
    name: 'capacitor:cache-file',

    async canShare() {
      try {
        return (await share.canShare()).value;
      } catch {
        return false;
      }
    },

    async write(_filename, bytes) {
      const path = temporaryReportPath(Date.now());
      const { uri } = await filesystem.writeFile({
        path,
        data: base64From(bytes),
        directory: Directory.Cache,
        recursive: true,
      });
      return { uri, path };
    },

    async view(handle) {
      await viewer.openDocumentFromLocalPath({ path: handle.uri });
    },

    async share(handle, options) {
      try {
        await share.share({
          title: options.title,
          dialogTitle: options.title,
          files: [handle.uri],
        });
        return 'shared';
      } catch (error) {
        if (isShareCancellation(error)) return 'cancelled';
        throw error;
      }
    },

    async discard(handle) {
      try {
        await filesystem.deleteFile({ path: handle.path, directory: Directory.Cache });
      } catch {
        // A file the system already reclaimed is the outcome we wanted.
      }
    },

    async sweepStale(now, keep = null) {
      let removed = 0;
      try {
        const { files } = await filesystem.readdir({ path: '', directory: Directory.Cache });
        for (const file of files) {
          // **EXACT PREFIX, FILES ONLY, AND NEVER THE CURRENT ARTIFACT.**
          // Anything else in the cache belongs to somebody else.
          if (file.type !== 'file') continue;
          if (!file.name.startsWith(REPORT_TEMP_PREFIX)) continue;
          if (keep && file.name === keep.path) continue;
          if (now - file.mtime < REPORT_TEMP_MAX_AGE_MS) continue;
          try {
            await filesystem.deleteFile({ path: file.name, directory: Directory.Cache });
            removed += 1;
          } catch {
            // Another process may have taken it first. Not our problem.
          }
        }
      } catch {
        // No cache directory yet, or no permission to list it. Sweeping is
        // housekeeping: it never fails a report.
      }
      return removed;
    },
  };
}

/**
 * The browser implementation.
 *
 * **A TAB IS NOT A PHONE.** There is no cache directory and no share sheet
 * guarantee, so the "file" is an object URL: View opens it (browsers render PDFs
 * natively) and Share uses the Web Share API **only when it will genuinely take
 * a file**, falling back to an ordinary download rather than pretending a native
 * sheet exists.
 */
export function createWebReportFile(
  options: {
    open?: (url: string) => void;
    navigatorLike?: {
      share?: (data: { files?: File[]; title?: string }) => Promise<void>;
      canShare?: (data: { files?: File[] }) => boolean;
    };
    createObjectUrl?: (blob: Blob) => string;
    revokeObjectUrl?: (url: string) => void;
    download?: (url: string, filename: string) => void;
  } = {},
): ReportFilePort {
  const openUrl =
    options.open ??
    ((url: string) => {
      window.open(url, '_blank', 'noopener,noreferrer');
    });
  const nav: NonNullable<typeof options.navigatorLike> =
    options.navigatorLike ?? (typeof navigator === 'undefined' ? {} : navigator);
  const create =
    options.createObjectUrl ??
    ((blob: Blob) => (typeof URL.createObjectURL === 'function' ? URL.createObjectURL(blob) : ''));
  const revoke =
    options.revokeObjectUrl ??
    ((url: string) => {
      if (typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(url);
    });
  const download =
    options.download ??
    ((url: string, filename: string) => {
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      anchor.rel = 'noopener';
      anchor.click();
    });

  const files = new Map<string, { blob: Blob; filename: string }>();

  return {
    name: 'web:object-url',

    canShare() {
      return Promise.resolve(typeof nav.share === 'function');
    },

    write(filename, bytes) {
      const blob = new Blob([bytes as BlobPart], { type: PDF_MIME });
      const uri = create(blob);
      files.set(uri, { blob, filename });
      return Promise.resolve({ uri, path: filename });
    },

    view(handle) {
      openUrl(handle.uri);
      return Promise.resolve();
    },

    async share(handle, shareOptions) {
      const entry = files.get(handle.uri);
      if (entry && typeof nav.share === 'function' && typeof File === 'function') {
        const file = new File([entry.blob], entry.filename, { type: PDF_MIME });
        if (nav.canShare?.({ files: [file] }) !== false) {
          try {
            await nav.share({ files: [file], title: shareOptions.title });
            return 'shared';
          } catch (error) {
            // `AbortError` is the browser's word for "the user dismissed it".
            if (error instanceof Error && error.name === 'AbortError') return 'cancelled';
            // Anything else falls through to the download, which always works.
          }
        }
      }
      download(handle.uri, entry?.filename ?? 'report.pdf');
      return 'shared';
    },

    discard(handle) {
      files.delete(handle.uri);
      revoke(handle.uri);
      return Promise.resolve();
    },

    sweepStale() {
      // Object URLs die with the document; there is nothing on disk to sweep.
      return Promise.resolve(0);
    },
  };
}

/**
 * Chosen once, like every other port (ARCHITECTURE §3).
 *
 * `unknown` takes the web path deliberately: an object URL in a WebView still
 * shows the document, while calling a plugin that may not be there would fail
 * with nothing to fall back to.
 */
export function selectReportFile(): ReportFilePort {
  return detectPlatform() === 'native' ? createCapacitorReportFile() : createWebReportFile();
}
